import { z } from 'zod';

import { MONEY_DECIMALS } from '../../billing/money/money';

/**
 * How a discount rule reduces a price (`03-courses-discounts.md` §Data model).
 */
export const DISCOUNT_TYPES = ['percent', 'fixed'] as const;

export const DiscountTypeSchema = z.enum(DISCOUNT_TYPES);

export type DiscountType = (typeof DISCOUNT_TYPES)[number];

const TYPE_SET: ReadonlySet<string> = new Set(DISCOUNT_TYPES);

export function isDiscountType(value: string): value is DiscountType {
  return TYPE_SET.has(value);
}

/** A percent discount can take a price to zero and no further (matrix row 13). */
export const MAX_PERCENT_DISCOUNT = 100;

/**
 * Money and percentages are both stored to two places; a third is unrenderable.
 *
 * Re-exported from billing's `MONEY_DECIMALS` rather than declared again: the
 * precision is one platform-wide fact, and two constants both spelled `2` are
 * two places to change it and one place to forget (U3).
 */
export const DISCOUNT_AMOUNT_DECIMALS = MONEY_DECIMALS;

const AMOUNT_STEP = 10 ** DISCOUNT_AMOUNT_DECIMALS;

/**
 * A discount's magnitude, before its type is known.
 *
 * Positive, because a rule that grants nothing is a rule somebody mis-typed, and
 * a *negative* one would be a surcharge issued through the discount pipeline.
 * The two-decimal ceiling is checked with integer arithmetic rather than a
 * regex, so `1.1` (which is not exactly representable) is accepted and `1.005`
 * is not.
 */
export const DiscountAmountSchema = z
  .number()
  .positive()
  .refine((value) => Number.isInteger(Math.round(value * AMOUNT_STEP * 1000) / 1000), {
    message: `Must have at most ${DISCOUNT_AMOUNT_DECIMALS} decimal places.`,
  });

/**
 * Whether an amount is legal **for its type**.
 *
 * Split from the schema rather than folded into a `.refine` on the rule object,
 * because a `.refine` on an object schema cannot report which field is wrong and
 * — as P16b found for settings — makes every partial write re-validate a whole
 * it does not have. The rule schemas call this and attach the error to `value`.
 *
 * `fixed` is deliberately uncapped here: the ceiling is the product's price,
 * which this layer does not know, and the clamp happens at redemption (row 13).
 */
export function isValidDiscountAmount(type: DiscountType, value: number): boolean {
  if (!DiscountAmountSchema.safeParse(value).success) {
    return false;
  }

  return type === 'percent' ? value <= MAX_PERCENT_DISCOUNT : true;
}
